import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Papa } from 'ngx-papaparse';
import { AccessibilityCheckRow } from '../upload-accessibility-checks.component';

interface FieldMapping {
    csvColumn: string;
    checkField: string;
    label: string;
    required: boolean;
}

@Component({
    selector: 'app-upload-mapping',
    imports: [CommonModule, FormsModule],
    templateUrl: './upload-mapping.component.html',
    styleUrls: ['./upload-mapping.component.scss'],
})
export class UploadMappingComponent {
    @Output() complete = new EventEmitter<AccessibilityCheckRow[]>();

    file: File | null = null;
    csvHeaders: string[] = [];
    csvData: Record<string, string>[] = [];
    previewRows: Record<string, string>[] = [];

    fieldMappings: FieldMapping[] = [
        { csvColumn: '', checkField: 'id', label: 'Database ID', required: true },
        { csvColumn: '', checkField: 'name', label: 'Name', required: false },
        { csvColumn: '', checkField: 'description', label: 'Description', required: false },
        { csvColumn: '', checkField: 'friendly_name', label: 'Friendly Name', required: false },
        { csvColumn: '', checkField: 'impact', label: 'Impact', required: false },
        { csvColumn: '', checkField: 'difficulty', label: 'Difficulty', required: false },
        { csvColumn: '', checkField: 'article_url', label: 'Article URL', required: false },
    ];

    showPreview = false;
    errorMessage = '';

    constructor (private papa: Papa) {}

    onFileSelected (event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.file = input.files[0];
            this.parseCSV();
        }
    }

    parseCSV (): void {
        if (!this.file) {
            return;
        }

        this.errorMessage = '';

        this.papa.parse(this.file, {
            header: true,
            skipEmptyLines: true,
            complete: (result) => {
                if (result.errors.length > 0) {
                    this.errorMessage = `CSV parsing error: ${result.errors[0].message}`;
                    return;
                }

                this.csvHeaders = result.meta.fields || [];
                this.csvData = result.data;
                this.previewRows = this.csvData.slice(0, 5);

                this.autoDetectMappings();
                this.showPreview = true;
            },
            error: (error) => {
                this.errorMessage = `Failed to parse CSV: ${error.message}`;
            },
        });
    }

    autoDetectMappings (): void {
        this.fieldMappings.forEach(mapping => {
            const exactMatch = this.csvHeaders.find(h =>
                h.toLowerCase() === mapping.checkField.toLowerCase(),
            );

            if (exactMatch) {
                mapping.csvColumn = exactMatch;
                return;
            }

            const partialMatch = this.csvHeaders.find(h =>
                h.toLowerCase().includes(mapping.checkField.toLowerCase().replace('_', ' ')),
            );

            if (partialMatch) {
                mapping.csvColumn = partialMatch;
            }
        });
    }

    canProceed (): boolean {
        if (!this.showPreview) {
            return false;
        }

        const requiredMapped = this.fieldMappings
            .filter(m => m.required)
            .every(m => m.csvColumn);

        return requiredMapped;
    }

    proceed (): void {
        const mappedData: AccessibilityCheckRow[] = this.csvData.map((row, index) => {
            const mappedRow: Partial<AccessibilityCheckRow> = {
                rowNumber: index + 2, // +2 because row 1 is header, and we're 0-indexed
            };

            this.fieldMappings.forEach(mapping => {
                if (mapping.csvColumn && row[mapping.csvColumn] !== undefined) {
                    let value: string | number = row[mapping.csvColumn];

                    if (mapping.checkField === 'id') {
                        value = parseInt(value, 10);
                    }

                    (mappedRow as Record<string, string | number>)[mapping.checkField] = value;
                }
            });

            return mappedRow as AccessibilityCheckRow;
        });

        this.complete.emit(mappedData);
    }
}
